import type {
  AbsoluteWorkspacePath,
  IDisposable,
} from "@codesandbox/pitcher-client";
import { DisposableStore } from "@codesandbox/pitcher-common";
import { FS_SCHEME, useEnvironmentInterface } from "environment-interface";
import type { TabbedPanel, Tab } from "features/types/tabs";
import { usePitcher } from "features/utils/usePitcher";
import { Selection } from "monaco-editor";
import { useEffect } from "react";
import type { IUntypedEditorInput } from "vscode/services";
import {
  IEditorService,
  IWorkspaceContextService,
  StandaloneServices,
} from "vscode/services";

import { useActiveFile } from "hooks/useActiveFile";

import { createEditorInputFromTab } from "./editorSync";
import { useActiveCodeEditorSelection } from "./utils";

const ACTIVE_FILE_DEBOUNCE = 500;

/**
 * Responsible for VSCode open files synced back to CodeSandbox
 */
export function useActiveFileSync({
  topLeftPanel,
}: {
  topLeftPanel?: TabbedPanel;
  ignoredTabTypes?: Set<Tab["type"]>;
}) {
  const pitcher = usePitcher();
  const { activeCodeEditor, selection } = useActiveCodeEditorSelection(1000);
  const [activeFile, setActiveFileActions] = useActiveFile();
  const { monacoEditor } = useEnvironmentInterface();

  async function initializeVSCode(): Promise<IDisposable> {
    const disposableStore = new DisposableStore();
    await monacoEditor.getExtensionApi();
    const editorService = StandaloneServices.get(IEditorService);
    const workspaceCtxService = StandaloneServices.get(
      IWorkspaceContextService,
    );

    if (activeFile) {
      // Load initial file
      const resource = workspaceCtxService
        .getWorkspace()
        .folders[0].toResource(activeFile.path);

      const input: IUntypedEditorInput = {
        resource,
        options: {
          selection:
            activeFile.initialSelection &&
            new Selection(
              activeFile.initialSelection.startLineNumber,
              activeFile.initialSelection.startColumn,
              activeFile.initialSelection.endLineNumber,
              activeFile.initialSelection.endColumn,
            ),
        },
      };
      // Run a tick later because VSCode wants to close everything the moment
      // it loads. It's a race condition.
      requestAnimationFrame(() => {
        editorService.openEditor(input);
      });
    } else if (
      editorService.visibleEditors.filter(
        (e) => e.resource?.scheme === FS_SCHEME,
      ).length === 0
    ) {
      // No visible editor panes, let's try to open the default file
      const currentTab = topLeftPanel?.tabs[0];
      if (currentTab) {
        const input = createEditorInputFromTab(currentTab);
        if (input) {
          // Run a tick later because VSCode wants to close everything the moment
          // it loads. It's a race condition.
          requestAnimationFrame(() => {
            editorService.openEditor(input);
          });
        }
      }
    }

    return disposableStore;
  }

  useEffect(() => {
    if (!activeCodeEditor) {
      setActiveFileActions.unset();
      return;
    }

    const resource = activeCodeEditor.getModel()?.uri;
    if (resource?.scheme !== FS_SCHEME) {
      setActiveFileActions.unset();
      return;
    }

    const relativePath = pitcher.clients.fs.absoluteToRelativeWorkspacePath(
      resource.path as AbsoluteWorkspacePath,
    );

    // Do it 500ms later as it causes a re-render for many components, which means a less
    // snappy effect if it hogs the main thread.
    const timeoutId = setTimeout(() => {
      setActiveFileActions.set(relativePath, selection ?? undefined);
    }, ACTIVE_FILE_DEBOUNCE);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [activeCodeEditor, selection]);

  useEffect(() => {
    const disposable = initializeVSCode();
    return () => {
      disposable.then((d) => d.dispose());
    };
  }, []);
}
